import { useEffect, useState } from 'react'

import { useFloorPlan } from '../hooks/useFloorPlan'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useLightMode } from '@/hooks/useLightMode'
import { useSelector } from 'react-redux'

import { Box, Flex } from '@chakra-ui/react'

import { selectFacilityDrawerOpen, selectSideNavOpen } from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import { DarkRemoveCheckPointModal } from '../dark/DarkRemoveCheckPointModal'
import {
  DarkDevicePopoverContent,
  DarkFloorPlanPopover,
  DarkTailgatePopoverContent,
  DevicePopoverContent,
  FloorPlanPopover,
  TailgatePopoverContent,
} from '../floor-plan-popover'
import { useImageSize } from '../hooks'
import { DarkContainer } from '../styles/DarkStyledContainer'
import { Container } from '../styles/StyledContainer'
import { TailgateConnectionModals } from '../tailgate-connection'
import { FloorI, TourPlanI } from '../types/types'
import { getFloorPlanVisualColors } from '../utils/floorPlanVisuals'
import { getRailWidth } from '../utils/railWidth'
import { FloorChartContainer } from './FloorChartContainer'
import { FloorPlanPlaceholder } from './FloorPlanPlaceholder'
import { FloorPlanRightRail } from './FloorPlanRightRail'
import { RemoveCheckPointModal } from './RemoveCheckPointModal'

interface FloorPlanIProps {
  size: {
    width: number
    height: number
  }
  facilityId: string
  currentFloorId: string
  tourPlans: TourPlanI[]
  floors: FloorI[]
  onReorderFloors?: (floorIds: string[]) => void
}

export const FloorPlan = ({
  size,
  facilityId,
  currentFloorId,
  tourPlans,
  floors,
  onReorderFloors,
}: FloorPlanIProps) => {
  const { width, height } = size
  const mode = useLightMode()
  const isSideNavOpen = useSelector(selectSideNavOpen)
  const drawerOpen = useSelector(selectFacilityDrawerOpen)
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()
  const isFacilitiesRebrandEnabled = useFeatureFlag(
    FeatureFlag.facilitiesRebrand
  )
  const isGuardTour =
    drawerOpen === 'checkpoints' || drawerOpen === 'tour-plans'

  const [isPanelExpanded, setIsPanelExpanded] = useState(true)

  const {
    deviceHoverItem,
    devices,
    forceCloseSignal,
    forceCloseHoverCards,
    imageUrl,
    isDeviceHovering,
    isRemoveCheckPointOpen,
    isTailgateHovering,
    isFloorDataLoading,
    connections,
    isConnectionsLoading,
    connectDevices,
    disconnectDevices,
    getConnection,
    onRemoveCheckPointClose,
    selectedDevice,
    tailgateHoverItem,
  } = useFloorPlan()
  const { imageSize } = useImageSize(imageUrl)

  const togglePanelExpansion = () => {
    setIsPanelExpanded(!isPanelExpanded)
    // Reflows the canvas under a stationary pointer, which never fires a
    // mouseout — clear any open hover card explicitly.
    forceCloseHoverCards()
  }

  const rightPanelWidth = getRailWidth({
    drawerOpen,
    isFacilitiesRebrandEnabled,
    isPanelExpanded,
  })

  const chartInsets = {
    left: isSideNavOpen ? 240 : 60,
    top: 65,
    right: rightPanelWidth,
    bottom: 0,
  }
  const chartSize = [width - rightPanelWidth, height]
  const visualVariant = isFacilitiesRebrandEnabled ? 'rebrand' : 'legacy'
  const FloorPlanContainer = isFacilitiesRebrandEnabled
    ? DarkContainer
    : Container
  const floorPlanColors = getFloorPlanVisualColors(mode)

  useEffect(() => {
    const numberOfDevices = devices.length
    isFloorDataLoading
      ? mixpanel.time_event('Page Load: Floor Plan View')
      : mixpanel.track('Page Load: Floor Plan View', {
          number_of_devices: numberOfDevices,
        })
  }, [isFloorDataLoading])

  return (
    <Box h='100%'>
      <FloorPlanContainer
        {...(isFacilitiesRebrandEnabled && { $colorScheme: mode })}
      >
        <Flex alignItems='stretch' h='100%'>
          {/* The canvas declares its own ground rather than letting the
              global body background show through, so the floor plan can go
              dark without touching a rule every other page shares. Off the
              flag it stays unset, which is byte-for-byte today. */}
          <Flex
            flexGrow={1}
            h={isFacilitiesRebrandEnabled ? '100%' : undefined}
            justifyContent='center'
            minW={isFacilitiesRebrandEnabled ? 0 : undefined}
            pos={isFacilitiesRebrandEnabled ? 'relative' : 'static'}
            {...(isFacilitiesRebrandEnabled && { bg: floorPlanColors.canvas })}
          >
            <FloorPlanPlaceholder
              fetching={isFloorDataLoading}
              imageSize={imageSize}
              imageUrl={imageUrl}
              textColor={floorPlanColors.emptyStateText}
              visualVariant={visualVariant}
            />
            {imageSize && (
              <FloorChartContainer
                chartInsets={chartInsets}
                chartSize={chartSize}
                colorScheme={mode}
                connections={connections}
                devices={devices}
                imageSize={imageSize}
                imageUrl={imageUrl}
                isGuardTour={isGuardTour}
                showBinIcon={isEditMode && !isGuardTour}
                visualVariant={visualVariant}
              />
            )}
          </Flex>
          <Flex position='relative' w={`${rightPanelWidth}px`}>
            <FloorPlanRightRail
              currentFloorId={currentFloorId}
              facilityId={facilityId}
              floors={floors}
              isPanelExpanded={isPanelExpanded}
              onReorderFloors={onReorderFloors}
              onToggleExpansion={togglePanelExpansion}
              tourPlans={tourPlans}
            />
          </Flex>
        </Flex>
      </FloorPlanContainer>
      {isFacilitiesRebrandEnabled ? (
        <>
          <DarkFloorPlanPopover
            elementId={tailgateHoverItem?.elementId}
            forceCloseSignal={forceCloseSignal}
            isHovering={isTailgateHovering}
            kind='tailgate'
          >
            <DarkTailgatePopoverContent
              cameraName={tailgateHoverItem?.cameraName || ''}
              colorScheme={mode}
              connection={tailgateHoverItem?.connection}
              deviceName={tailgateHoverItem?.doorName || ''}
            />
          </DarkFloorPlanPopover>
          <DarkFloorPlanPopover
            elementId={deviceHoverItem?.elementId}
            forceCloseSignal={forceCloseSignal}
            isHovering={isDeviceHovering}
            kind='device'
          >
            <DarkDevicePopoverContent
              colorScheme={mode}
              deviceId={deviceHoverItem?.deviceId}
              deviceName={deviceHoverItem?.deviceName}
              deviceType={deviceHoverItem?.deviceType}
            />
          </DarkFloorPlanPopover>
        </>
      ) : (
        <>
          <FloorPlanPopover
            elementId={tailgateHoverItem?.elementId}
            forceCloseSignal={forceCloseSignal}
            isHovering={isTailgateHovering}
            kind='tailgate'
          >
            <TailgatePopoverContent
              cameraName={tailgateHoverItem?.cameraName || ''}
              connection={tailgateHoverItem?.connection}
              deviceName={tailgateHoverItem?.doorName || ''}
            />
          </FloorPlanPopover>
          <FloorPlanPopover
            elementId={deviceHoverItem?.elementId}
            forceCloseSignal={forceCloseSignal}
            isHovering={isDeviceHovering}
            kind='device'
          >
            <DevicePopoverContent
              deviceId={deviceHoverItem?.deviceId}
              deviceName={deviceHoverItem?.deviceName}
              deviceType={deviceHoverItem?.deviceType}
            />
          </FloorPlanPopover>
        </>
      )}
      {/* Opened from a checkpoint pin on the chart, which is legacy in both
          branches, so the flag is read here rather than inside a dark root. */}
      {isFacilitiesRebrandEnabled ? (
        <DarkRemoveCheckPointModal
          checkpoint={selectedDevice}
          facilityId={facilityId}
          floorId={currentFloorId}
          isOpen={isRemoveCheckPointOpen}
          onClose={onRemoveCheckPointClose}
          tourPlans={tourPlans}
        />
      ) : (
        <RemoveCheckPointModal
          checkpoint={selectedDevice}
          facilityId={facilityId}
          floorId={currentFloorId}
          isOpen={isRemoveCheckPointOpen}
          onClose={onRemoveCheckPointClose}
          tourPlans={tourPlans}
        />
      )}
      <TailgateConnectionModals
        connectDevices={connectDevices}
        connections={connections}
        disconnectDevices={disconnectDevices}
        getConnection={getConnection}
        isConnectionsLoading={isConnectionsLoading}
      />
    </Box>
  )
}
